import React, {
  ForwardedRef,
  ReactNode,
  forwardRef,
  useEffect,
  useState,
  ForwardRefExoticComponent,
  RefAttributes,
} from 'react';
import * as ReactDOM from 'react-dom';

export interface PortalProps {
  children?: ReactNode;
  container?: Element | (() => Element | null) | null;
}

function getContainer(container: PortalProps['container']) {
  return typeof container === 'function' ? container() : container;
}

export const Portal = forwardRef(function Portal(props: PortalProps, forwardedRef: ForwardedRef<Element>) {
  const { children, container } = props;
  const [mountNode, setMountNode] = useState<ReturnType<typeof getContainer>>(null);

  useEffect(() => {
    setMountNode(getContainer(container) || document.body);
  }, [container]);

  const setRef = (ref: any, value: any) => {
    if (typeof ref === 'function') {
      ref(value);
    } else if (ref) {
      ref.current = value;
    }
  };

  useEffect(() => {
    if (mountNode) {
      setRef(forwardedRef, mountNode);
      return () => {
        setRef(forwardedRef, null);
      };
    }

    return undefined;
  }, [forwardedRef, mountNode]);

  return <>{mountNode ? ReactDOM.createPortal(children, mountNode) : mountNode}</>;
}) as ForwardRefExoticComponent<PortalProps & RefAttributes<Element>>;

export default Portal;
